<template>
  <div>
    <div class="row mb-2 align-items-center">
      <div class="col-md-6">
        <span v-if="hasQueried" class="text-muted small">
          Showing {{ recordRangeStart }}–{{ recordRangeEnd }} of {{ totalRecords }} {{ label }}{{ totalRecords !== 1 ? 's' : '' }}
          &nbsp;|&nbsp;
          Page size:
          <select v-model="selectedPageSize" @change="changePageSize" class="form-control form-control-sm d-inline-block" style="width: auto;">
            <option :value="100">100</option>
            <option :value="500">500</option>
            <option :value="1000">1000</option>
          </select>
        </span>
      </div>
      <div class="col-md-6 text-right">
        <button class="btn btn-info btn-sm" @click="loadFirstPage" :disabled="loading">
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i> Load {{ label | capitalize }}s
        </button>
      </div>
    </div>

    <PageNavigator
      v-if="hasQueried"
      :disableControls="loading"
      :currentPage="pageNumber"
      :totalPages="totalPages"
      @nextPage="nextPage"
      @previousPage="previousPage"
      @firstPage="firstPage"
      @lastPage="lastPage"
    />

    <div v-if="loading" style="padding: 4em; text-align: center;">
      <loading :active="true" :can-cancel="false"></loading>
    </div>

    <div v-else-if="sessions.length > 0" class="table-responsive table-bordered mt-2">
      <table class="table text-nowrap">
        <thead>
          <tr>
            <!-- Column headers injected by the parent -->
            <slot name="columns" />
          </tr>
        </thead>
        <tbody>
          <tr v-for="session in sessions" :key="session.objKey">
            <!-- Row cells injected by the parent; session data + helpers are exposed via slot scope -->
            <slot
              name="row"
              :session="session"
              :lookupCpe="lookupCpe"
              :cpeMacAddresses="cpeMacAddresses"
              :humanizeDuration="humanizeDuration"
              :getTimeClass="getTimeClass"
              :formatBytes="formatBytes"
            />
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="hasQueried" class="alert alert-info mt-2">
      <strong>No {{ label }}s found.</strong>
    </div>

    <div v-else class="alert alert-secondary mt-2">
      <strong>Click "Load {{ label | capitalize }}s" to fetch data.</strong>
    </div>

    <PageNavigator
      v-if="hasQueried && totalPages > 0"
      class="mt-2"
      :disableControls="loading"
      :currentPage="pageNumber"
      :totalPages="totalPages"
      @nextPage="nextPage"
      @previousPage="previousPage"
      @firstPage="firstPage"
      @lastPage="lastPage"
    />
  </div>
</template>

<script>
import moment from 'moment';
import PageNavigator from '@/components/GeneralComponents/PageNavigator';
import { Circle } from 'vue-loading-spinner';

export default {
  components: {
    PageNavigator,
    loading: Circle,
  },
  filters: {
    capitalize(value) {
      if (!value) return '';
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
  props: {
    url: {
      type: String,
      required: true,
    },
    queryParams: {
      type: Object,
      default: () => ({}),
    },
    // Label used in record count and button text, e.g. 'session', 'record'
    label: {
      type: String,
      default: 'session',
    },
    pageSize: {
      type: Number,
      default: 100,
    },
  },
  data() {
    return {
      sessions: [],
      loading: false,
      hasQueried: false,
      pageNumber: 0,
      totalPages: 0,
      totalRecords: 0,
      selectedPageSize: this.pageSize,
      cpeMacAddresses: {},
    };
  },
  computed: {
    recordRangeStart() {
      return this.totalRecords === 0 ? 0 : this.pageNumber * this.selectedPageSize + 1;
    },
    recordRangeEnd() {
      return Math.min((this.pageNumber + 1) * this.selectedPageSize, this.totalRecords);
    },
  },
  methods: {
    loadFirstPage() {
      this.pageNumber = 0;
      this.fetchSessions();
    },
    changePageSize() {
      this.pageNumber = 0;
      this.fetchSessions();
    },
    fetchSessions() {
      this.loading = true;
      const params = new URLSearchParams({
        ...this.queryParams,
        order: '-startTime',
        page: this.pageNumber + 1,
        pageSize: this.selectedPageSize,
      });
      this.$http.get(`${this.url}/paged?${params.toString()}`).then(
        (response) => {
          const page = response.data;
          this.sessions = page.results || [];
          this.totalRecords = page.totalRecords || 0;
          this.totalPages = page.totalPages > 0 ? page.totalPages - 1 : 0;
          this.hasQueried = true;
          this.loading = false;
        },
        (error) => {
          this.showError('Error fetching sessions', error);
          console.error(error);
          this.loading = false;
        }
      );
    },
    firstPage() {
      this.pageNumber = 0;
      this.fetchSessions();
    },
    lastPage() {
      this.pageNumber = this.totalPages;
      this.fetchSessions();
    },
    nextPage(num) {
      this.pageNumber = Math.min(this.pageNumber + num, this.totalPages);
      this.fetchSessions();
    },
    previousPage(num) {
      this.pageNumber = Math.max(this.pageNumber - num, 0);
      this.fetchSessions();
    },
    lookupCpe(session, field = 'cpeAddress') {
      if (!this.cpeMacAddresses[session[field]]) {
        if (
          field === 'cpeAddress' &&
          session[field] &&
          session[field] !== '' &&
          !session.macDetails &&
          !session.fetchingMac
        ) {
          this.$set(session, 'fetchingMac', true);
          this.$http
            .get(
              this.$config.aimsAPI +
                'system/tools/macaddress/' +
                session[field] +
                '/' +
                session.username
            )
            .then(
              (response) => {
                session.fetchingMac = false;
                this.$set(this.cpeMacAddresses, session[field], response.data);
              },
              (error) => {
                session.fetchingMac = false;
                this.$set(this.cpeMacAddresses, session[field], 'Failed to fetch MAC details');
                console.error(error);
              }
            );
        }
      }
    },
    humanizeDuration(seconds) {
      return moment.duration(seconds, 'seconds').humanize();
    },
    getTimeClass(seconds) {
      return seconds > 3600 ? 'badge bg-green' : 'badge bg-yellow';
    },
    formatBytes(bytes) {
      if (bytes.toString().length > 9) return this.sizeBytesToGb(bytes) + ' GB';
      if (bytes.toString().length > 6) return this.sizeBytesToMb(bytes) + ' MB';
      return this.sizeBytesToKb(bytes) + ' KB';
    },
  },
};
</script>
